/**
 * Creature figure + hull + prop stages (art §4.1 uber-material, §4.2 props, §4.3 inverted hull; cheatsheet §3.10 r3).
 *
 * Vertex: 2-bone linear-blend skin + the art §4.1-J lagged-palette smear in one `skinSmear` helper shared by the figure
 * and the hull (outlines stay attached). Fragment: A facet + Cennini bands → B tempera noise → C pattern → D fur strokes
 * → E hatching → F rim → H overlays → G barycentric lines → I sketch reveal. Every derivative is taken at the top of the
 * fragment entry (WGSL uniform control flow).
 */
import tgpu from 'typegpu';
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import { MAX_BONES, MAX_PROPS, MAX_SLOTS, QF, frameLayout, propLayout, skinLayout } from '../gpu/layouts';
import { fbm2o, hatchLines, lineMask, luma, ramp3, rnd3, rnd33, sdTaper, vnoise, vnoise3, worley2, worley3 } from './lib';
import {
  P_BOLE, P_COPPER, P_FOLIUM, P_GLINT, P_GOLD, P_GOLD_DEEP, P_GOLD_LIGHT, P_INK, P_INK_FADED, P_IRON, P_LAPIS_PALE,
  P_LEAD_WHITE, P_UMBER, P_VELLUM, P_VELLUM_AGED,
} from './pigments';

/** Region circumference in H (art §4.1-B `circ(region)`): skull, muzzle, ears, neck, torso, upper arms, forearms,
 *  thighs, shins, tail, extras. */
const CIRC = tgpu.const(d.arrayOf(d.f32, 11), [0.9, 0.5, 0.25, 0.6, 1.0, 0.35, 0.3, 0.45, 0.35, 0.15, 0.6]);
/** Material id → fixed ramp index in `mats` (255 = not a fixed-ramp material). */
const MATR_OF = tgpu.const(d.arrayOf(d.u32, 21), [255, 255, 10, 255, 255, 255, 255, 255, 255, 0, 1, 2, 255, 11, 6, 3, 4, 9, 5, 255, 255]);

// ── vertex stages ───────────────────────────────────────────────────────────────────────────────────────────────────

/** 2-bone linear blend skin + art §4.1-J lagged-palette smear. Shared by the figure and the hull stage. */
export const skinSmear = tgpu.fn([d.u32, d.vec3f, d.vec4u, d.u32], d.vec3f)((slot, pos, skin, region) => {
  'use gpu';
  const a = slot * MAX_BONES + skin.x;
  const b = slot * MAX_BONES + skin.y;
  const wA = d.f32(skin.z) / 255.0;
  const p = d.vec4f(pos, 1);
  let w = std.mix(skinLayout.$.bones[b].mul(p).xyz, skinLayout.$.bones[a].mul(p).xyz, wA);
  const s = skinLayout.$.smear[slot];
  if (s.x > 0.0) {
    const q = std.mix(skinLayout.$.bonesLag[b].mul(p).xyz, skinLayout.$.bonesLag[a].mul(p).xyz, wA);
    const dv = w.sub(q);
    const len = std.length(dv);
    if (len > s.w * s.y) {
      const dir = dv.div(len);
      const origin = skinLayout.$.bones[a].mul(skinLayout.$.bindPivot[a]).xyz;
      const rel = w.sub(origin);
      const relLen = std.max(std.length(rel), 0.00001);
      const trailing = std.saturate(0.5 - 0.5 * std.dot(rel.div(relLen), dir));
      const skull = std.select(d.f32(1), d.f32(0.3), region === 0);
      w = w.sub(dir.mul(std.min(len * s.x, s.z) * trailing * skull));
    }
  }
  return w;
});

const FigureVaryings = {
  world: d.vec3f, rest: d.vec3f, pat: d.vec2f, bary: d.vec3f,
  material: d.interpolate('flat', d.u32), region: d.interpolate('flat', d.u32), slot: d.interpolate('flat', d.u32),
  edge: d.interpolate('flat', d.u32),
};

const baryOf = (corner: number) => {
  'use gpu';
  return d.vec3f(
    std.select(d.f32(0), d.f32(1), corner === 0),
    std.select(d.f32(0), d.f32(1), corner === 1),
    std.select(d.f32(0), d.f32(1), corner === 2),
  );
};

export const creatureVertex = tgpu.vertexFn({
  in: { pos: d.vec3f, pat: d.vec2f, skin: d.vec4u, info: d.vec4u, inst: d.builtin.instanceIndex },
  out: { position: d.builtin.position, ...FigureVaryings },
})((input) => {
  'use gpu';
  const slot = std.min(input.inst, MAX_SLOTS - 1);
  const w = skinSmear(slot, input.pos, input.skin, input.info.y);
  return {
    position: frameLayout.$.frame.viewProj.mul(d.vec4f(w, 1)),
    world: w, rest: input.pos, pat: input.pat, bary: baryOf(input.info.w),
    material: input.info.x, region: input.info.y, slot, edge: input.info.z | (input.skin.w << 8),
  };
});

/** Props (art §3.2 render layout with boneA = boneB = 0): one matrix from the props buffer; the look of the owner. */
export const propVertex = tgpu.vertexFn({
  in: { pos: d.vec3f, pat: d.vec2f, skin: d.vec4u, info: d.vec4u, inst: d.builtin.instanceIndex },
  out: { position: d.builtin.position, ...FigureVaryings },
})((input) => {
  'use gpu';
  const pi = std.min(input.inst, MAX_PROPS - 1);
  const m = propLayout.$.props[pi];
  const w = m.mul(d.vec4f(input.pos, 1)).xyz;
  const owner = std.min(propLayout.$.propInfo[pi].x, MAX_SLOTS - 1);
  return {
    position: frameLayout.$.frame.viewProj.mul(d.vec4f(w, 1)),
    world: w, rest: input.pos, pat: input.pat, bary: baryOf(input.info.w),
    material: input.info.x, region: input.info.y, slot: owner, edge: input.info.z | (input.skin.w << 8),
  };
});

/** Screen-space hull extrusion shared by creature and prop hulls (art §4.3; pitfall-16 guard). */
const extrude = (w: d.v3f, ns: d.v3f, widthCss: number, rest: d.v3f, zPush: number) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const clip = f.viewProj.mul(d.vec4f(w, 1));
  const nClip = f.viewProj.mul(d.vec4f(ns, 0)).xy;
  const vp = f.viewport.xy;
  const nPx = nClip.mul(vp);
  const dirPx = nPx.div(std.max(std.length(nPx), 0.000001));
  const side = std.mix(0.8, 1.3, std.saturate(0.5 - 0.5 * std.dot(ns, frameLayout.$.light.keyDir.xyz)));
  const press = 1.0 + 0.25 * (vnoise3(rest.mul(9.0)) * 2.0 - 1.0);
  const wPx = widthCss * f.viewport.z * side * press;
  const off = dirPx.mul(wPx * 2.0).div(vp).mul(clip.w);
  return d.vec4f(clip.xy.add(off), clip.z + zPush * clip.w, clip.w);
};

const HullOut = { position: d.builtin.position, slot: d.interpolate('flat', d.u32) };

export const hullVertex = tgpu.vertexFn({
  in: { pos: d.vec3f, nrm: d.vec4f, skin: d.vec4u, inst: d.builtin.instanceIndex },
  out: HullOut,
})((input) => {
  'use gpu';
  const slot = std.min(input.inst, MAX_SLOTS - 1);
  const w = skinSmear(slot, input.pos, input.skin, input.skin.w);
  const ns = std.normalize(skinLayout.$.bones[slot * MAX_BONES + input.skin.x].mul(d.vec4f(input.nrm.xyz, 0)).xyz);
  const width = skinLayout.$.looks[slot].line.x * input.nrm.w;
  return { position: extrude(w, ns, width, input.pos, 0.0004), slot };
});

/** Keyline hull (art §4.3): +1.5 px wider, leadWhite 85 %, zPush ×2 so the ink line wins where they overlap. */
export const keylineVertex = tgpu.vertexFn({
  in: { pos: d.vec3f, nrm: d.vec4f, skin: d.vec4u, inst: d.builtin.instanceIndex },
  out: HullOut,
})((input) => {
  'use gpu';
  const slot = std.min(input.inst, MAX_SLOTS - 1);
  const w = skinSmear(slot, input.pos, input.skin, input.skin.w);
  const ns = std.normalize(skinLayout.$.bones[slot * MAX_BONES + input.skin.x].mul(d.vec4f(input.nrm.xyz, 0)).xyz);
  const width = (skinLayout.$.looks[slot].line.x + 1.5) * std.max(input.nrm.w, 0.6);
  return { position: extrude(w, ns, width, input.pos, 0.0008), slot };
});

export const propHullVertex = tgpu.vertexFn({
  in: { pos: d.vec3f, nrm: d.vec4f, skin: d.vec4u, inst: d.builtin.instanceIndex },
  out: HullOut,
})((input) => {
  'use gpu';
  const pi = std.min(input.inst, MAX_PROPS - 1);
  const m = propLayout.$.props[pi];
  const owner = std.min(propLayout.$.propInfo[pi].x, MAX_SLOTS - 1);
  const w = m.mul(d.vec4f(input.pos, 1)).xyz;
  const ns = std.normalize(m.mul(d.vec4f(input.nrm.xyz, 0)).xyz);
  const width = skinLayout.$.looks[owner].line.x * 0.85 * input.nrm.w;
  return { position: extrude(w, ns, width, input.pos, 0.0004), slot: owner };
});

const figureMask = (slot: number) => {
  'use gpu';
  return std.select(d.f32(0.5), d.f32(1), skinLayout.$.looks[slot].fx.w > 0.5);
};

export const inkFragment = tgpu.fragmentFn({ in: { slot: d.interpolate('flat', d.u32) }, out: d.vec4f })((input) => {
  'use gpu';
  const look = skinLayout.$.looks[input.slot];
  // cursed → folium hull (art §4.1-H); ghost → gold outline
  const c = std.select(P_INK.$, P_FOLIUM.$, look.pattern.w === 4);
  return d.vec4f(c, figureMask(input.slot));
});

export const keylineFragment = tgpu.fragmentFn({ in: { slot: d.interpolate('flat', d.u32) }, out: d.vec4f })((input) => {
  'use gpu';
  return d.vec4f(P_LEAD_WHITE.$.mul(0.85), figureMask(input.slot));
});

// ── metal leaf (art §4.2-A gold, copper, silver) ────────────────────────────────────────────────────────────────────

const goldRamp = (e: number) => {
  'use gpu';
  // umber@0 · goldDeep@0.35 · gold@0.6 · goldLight@0.85 · glint@1
  const a = std.mix(P_UMBER.$, P_GOLD_DEEP.$, std.saturate(e / 0.35));
  const b = std.mix(a, P_GOLD.$, std.saturate((e - 0.35) / 0.25));
  const c = std.mix(b, P_GOLD_LIGHT.$, std.saturate((e - 0.6) / 0.25));
  return std.mix(c, P_GLINT.$, std.saturate((e - 0.85) / 0.15));
};

export const metalLeaf = (kind: number, N: d.v3f, V: d.v3f, L: d.v3f, rest: d.v3f, nO: d.v3f, time: number, bits: number) => {
  'use gpu';
  const R = std.reflect(std.neg(V), N);
  const e = std.saturate(R.y * 0.5 + 0.5 + 0.15 * std.sin(R.x * 3.0 + R.z * 2.0) + (rnd3(std.floor(nO.mul(30.0))) - 0.5) * 0.3);
  let base = goldRamp(e);
  if (kind === 19) {
    base = std.mix(P_UMBER.$, P_COPPER.$, std.saturate(e * 1.4)).add(P_GLINT.$.mul(std.saturate(e - 0.8) * 1.5));
  } else if (kind === 20) {
    base = std.mix(P_IRON.$.mul(0.6), P_LEAD_WHITE.$, std.saturate(e * 1.1));
  }
  // craquelure (High+): Worley edges on a planar projection show the red bole underneath
  if ((bits & 16) !== 0) {
    const nA = std.abs(nO);
    const pl = std.select(std.select(rest.xy, rest.xz, nA.y > nA.z), rest.zy, nA.x > nA.y && nA.x > nA.z);
    const cr = worley2(pl.mul(22.0));
    const crack = (1.0 - std.smoothstep(0.03, 0.08, cr.y)) * std.step(0.45, rnd3(d.vec3f(std.floor(pl.mul(22.0)), 3.0)));
    base = std.mix(base, P_BOLE.$, crack * 0.55);
  }
  // diagonal sheen sweep every 4 s (object space so it travels over the prop)
  const s = std.fract((rest.x + rest.y * 0.6) * 0.8 - time / 4.0);
  const sheen = std.exp(-std.pow((s - 0.5) / 0.06, 2.0)) * 0.8;
  // rare ~120 ms glints on facets facing the half vector (Medium+)
  const c = std.floor(rest.mul(40.0));
  const ph = std.fract(time * 0.7 + rnd3(c));
  const spark = std.step(0.985, rnd3(c.add(d.vec3f(7)))) * std.smoothstep(0.0, 0.05, ph) * (1.0 - std.smoothstep(0.05, 0.15, ph))
    * std.pow(std.max(std.dot(N, std.normalize(L.add(V))), 0.0), 4.0);
  const sparkK = std.select(d.f32(0), d.f32(1), (bits & 8) !== 0);
  return base.add(P_GLINT.$.mul(sheen + spark * 2.5 * sparkK));
};

// ── the uber-material ───────────────────────────────────────────────────────────────────────────────────────────────

export const creatureFragment = tgpu.fragmentFn({
  in: { fragPos: d.builtin.position, ...FigureVaryings },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const f = frameLayout.$.frame;
  const sl = frameLayout.$.light;
  const look = skinLayout.$.looks[input.slot];
  const time = f.timing.x;
  const pxScale = f.viewport.z;
  const mat = input.material;
  const region = std.min(input.region, 10);
  const bits = sl.qualityFlags.z;
  const circ = CIRC.$[region];

  // ── derivatives first (uniform control flow) ──
  const nW = std.cross(std.dpdx(input.world), std.dpdy(input.world));
  const nR = std.cross(std.dpdx(input.rest), std.dpdy(input.rest));
  const fwBary = std.fwidth(input.bary);
  const V = std.normalize(f.eye.xyz.sub(input.world));
  const nW1 = std.normalize(nW);
  const N = std.select(std.neg(nW1), nW1, std.dot(nW1, V) >= 0.0);
  const nO = std.normalize(nR);
  const L = sl.keyDir.xyz;
  const T1 = sl.keyDir.w;
  const T2 = sl.keyTint.w;

  // B: tempera / brush noise (rest-pose part coordinates; strokes elongated along the spine)
  const buv = d.vec2f(input.pat.x * 12.0, input.pat.y * circ * 40.0);
  const bn = vnoise(buv) * 0.65 + vnoise(buv.mul(2.1).add(d.vec2f(17, 3))) * 0.35;
  const jitter = bn * 2.0 - 1.0;
  // facet mosaic: a per-facet value wobble seeded by the rest-space facet normal (never swims)
  const facetH = rnd3(std.floor(nO.mul(37.0)).add(d.vec3f(d.f32(input.slot) * 13.0, 0, 0)));

  // A: half-Lambert + painted band borders
  const t = std.dot(N, L) * 0.5 + 0.5 + jitter * 0.06 + (facetH - 0.5) * 0.1;
  const aa = std.clamp(std.fwidth(t) * 1.5, 0.003, 0.05);
  const a1 = std.smoothstep(T1 - aa, T1 + aa, t);
  const a2 = std.smoothstep(T2 - aa, T2 + aa, t);
  const band = std.select(std.select(d.u32(2), d.u32(1), t < T2), d.u32(0), t < T1);

  // C: pattern coordinates (computed unconditionally so their derivatives are legal)
  const pp = look.patParams;
  const warpN = fbm2o(d.vec2f(input.rest.x * 3.0 + input.rest.z * 2.3, input.rest.y * 3.0));
  const sCoord = input.pat.x * 2.0 * pp.x + pp.y * warpN;
  const wave = 0.5 + 0.5 * std.sin(6.2831853 * sCoord);
  const aaW = std.clamp(std.fwidth(wave), 0.01, 0.3);
  const hf = look.headFrame;
  const qh = input.rest.sub(hf.xyz).div(std.max(hf.w, 0.01));
  const aaM = std.max(std.fwidth(qh.x), 0.004);

  // D: fur stroke lattice (~48 cells per H)
  const density = sl.hatch.w;
  const qS = d.vec2f(input.pat.x * 2.0, input.pat.y * circ).mul(34.0 * std.max(density, 0.3));
  const fwq = std.max(std.fwidth(qS.x), 0.0001);

  // E: hatch coordinates, per-facet dominant-axis planar projection of the rest position
  const nA = std.abs(nO);
  const uvX = d.vec2f(input.rest.z, input.rest.y);
  const uvY = d.vec2f(input.rest.x, input.rest.z);
  const uvZ = d.vec2f(input.rest.x, input.rest.y);
  const uvH = std.select(std.select(uvZ, uvY, nA.y > nA.z), uvX, nA.x > nA.y && nA.x > nA.z);
  const c1 = std.dot(uvH, d.vec2f(0.7071, 0.7071)) + vnoise(uvH.mul(8.0)) * 0.012;
  const c2 = std.dot(uvH, d.vec2f(-0.7071, 0.7071));
  const ppu1 = 1.0 / std.max(std.fwidth(c1), 0.000001);
  const ppu2 = 1.0 / std.max(std.fwidth(c2), 0.000001);

  // ── material ──
  let col = d.vec3f(0.5);
  let shaded = d.f32(1); //   0 = unshaded (eyes): no hatch, rim, mosaic
  let hatchK = d.f32(1);
  let rimK = d.f32(1);
  let stripeM = d.f32(0);
  if (mat === 0) {
    // fur: three ramps, each banded, mixed by the §2.6 masks
    const base = ramp3(look.rampBase[0].xyz, look.rampBase[1].xyz, look.rampBase[2].xyz, a1, a2);
    const stripe = ramp3(look.rampStripe[0].xyz, look.rampStripe[1].xyz, look.rampStripe[2].xyz, a1, a2);
    const belly = ramp3(look.rampBelly[0].xyz, look.rampBelly[1].xyz, look.rampBelly[2].xyz, a1, a2);
    const ptype = look.pattern.x;
    let bellyM = d.f32(0);
    if (ptype === 1 || ptype === 2) {
      const th = pp.z + jitter * 0.05;
      stripeM = std.smoothstep(th - aaW, th + aaW, wave);
      // dorsal stripe on torso and tail
      if (region === 4 || region === 9) {
        const dors = 1.0 - std.smoothstep(0.06, 0.09, std.abs(input.pat.y - 0.75));
        stripeM = std.max(stripeM, dors * 0.9);
      }
      // forehead «M» (§2.6), front of the skull only
      if ((look.pattern.z & 1) !== 0 && region === 0 && qh.z > 0.35) {
        const p = qh.xy;
        const wM = 0.035;
        let dm = sdTaper(p, d.vec2f(-0.32, 0.30), d.vec2f(-0.16, 0.55), wM, wM);
        dm = std.min(dm, sdTaper(p, d.vec2f(-0.16, 0.55), d.vec2f(0, 0.34), wM, wM));
        dm = std.min(dm, sdTaper(p, d.vec2f(0, 0.34), d.vec2f(0.16, 0.55), wM, wM));
        dm = std.min(dm, sdTaper(p, d.vec2f(0.16, 0.55), d.vec2f(0.32, 0.30), wM, wM));
        // two cheek lines
        dm = std.min(dm, sdTaper(d.vec2f(std.abs(p.x), p.y), d.vec2f(0.42, 0.02), d.vec2f(0.78, 0.12), 0.03, 0.012));
        stripeM = std.max(stripeM * std.smoothstep(0.2, 0.5, qh.y + 0.3), 1.0 - std.smoothstep(-aaM, aaM, dm));
      }
    } else if (ptype === 3) {
      const wv = worley3(d.vec3f(input.rest.x, input.rest.y * 0.6, input.rest.z).mul(pp.x));
      const r = pp.z + (wv.y - 0.5) * 0.16;
      stripeM = 1.0 - std.smoothstep(r - 0.04, r + 0.04, wv.x);
    } else if (ptype === 6) {
      // colour-point: ears, muzzle, tail, distal limbs
      let pm = d.f32(0);
      if (region === 2 || region === 1 || region === 9) { pm = 1.0; }
      if (region === 6 || region === 8) { pm = std.smoothstep(0.05, 0.2, input.pat.x); }
      stripeM = pm;
    } else if (ptype === 7) {
      const wv = worley3(input.rest.mul(pp.x).add(d.vec3f(warpN * 0.4)));
      // cell hash picks base / stripe / belly with weights 50 / 30 / 20
      stripeM = std.step(0.5, wv.y) * (1.0 - std.step(0.8, wv.y));
      bellyM = std.step(0.8, wv.y);
    }
    // belly: muzzle, chest and throat front (bicolor: wider + socks)
    const bw = std.select(pp.w, pp.w * 2.0, ptype === 5);
    if (region === 1) { bellyM = 1.0; }
    if (region === 4 || region === 3) {
      bellyM = std.max(bellyM, 1.0 - std.smoothstep(bw - 0.03, bw + 0.03, std.abs(input.pat.y - 0.25)));
    }
    if (ptype === 5 && (region === 6 || region === 8)) { bellyM = std.max(bellyM, std.smoothstep(0.08, 0.12, input.pat.x)); }
    col = std.mix(std.mix(base, stripe, stripeM), belly, bellyM * (1.0 - stripeM * 0.6));
    // D: fur strokes (Medium+): short tapered quill strokes in part space; denser on stripe borders and in shadow
    if ((bits & 1) !== 0) {
      const ink = look.strokeInk;
      const stripeEdge = 4.0 * stripeM * (1.0 - stripeM);
      const localMask = std.max(stripeEdge, std.select(std.select(d.f32(0.3), d.f32(0.6), band === 1), d.f32(0.9), band === 0));
      const cell0 = std.floor(qS.sub(d.vec2f(0.5)));
      let cov = d.f32(0);
      for (let oy = 0; oy <= 1; oy++) {
        for (let ox = 0; ox <= 1; ox++) {
          const cid = cell0.add(d.vec2f(d.f32(ox), d.f32(oy)));
          const h = rnd33(d.vec3f(cid, d.f32(look.pattern.y & 1023)));
          if (h.x < ink.w * localMask) {
            const cc = cid.add(d.vec2f(0.2)).add(h.yz.mul(0.6));
            const ang = (h.z - 0.5) * 0.5;
            const dir = d.vec2f(std.cos(ang), std.sin(ang));
            const ds = sdTaper(qS, cc.sub(dir.mul(0.45)), cc.add(dir.mul(0.45)), 0.15, 0.03);
            cov = std.max(cov, 1.0 - std.smoothstep(0.0, fwq * 1.2, ds));
          }
        }
      }
      cov = cov * (1.0 - std.smoothstep(0.35, 0.55, fwq));
      col = std.mix(col, ink.xyz, cov * 0.7);
    }
  } else if (mat === 1) {
    col = ramp3(look.rampFlesh[0].xyz, look.rampFlesh[1].xyz, look.rampFlesh[2].xyz, a1, a2);
  } else if (mat === 2) {
    // sclera: unshaded lead white (solemn stare reads in every light)
    col = P_LEAD_WHITE.$.mul(0.94);
    shaded = 0.0;
  } else if (mat === 3) {
    col = std.select(look.iris.xyz, look.iris.xyz.mul(0.65), input.pat.x > 0.78);
    shaded = 0.0;
  } else if (mat === 4) {
    col = d.vec3f(P_INK.$);
    shaded = 0.0;
  } else if (mat >= 5 && mat <= 8) {
    const di = (mat - 5) * 3;
    col = ramp3(look.rampDye[di].xyz, look.rampDye[di + 1].xyz, look.rampDye[di + 2].xyz, a1, a2);
    rimK = 0.8;
  } else if (mat === 12 || mat === 19 || mat === 20) {
    col = metalLeaf(mat, N, V, L, input.rest, nO, time, bits);
    hatchK = 0.0;
    rimK = 0.3;
  } else if (mat === 11) {
    const ri = MATR_OF.$[mat] * 3;
    col = ramp3(frameLayout.$.mats[ri].xyz, frameLayout.$.mats[ri + 1].xyz, frameLayout.$.mats[ri + 2].xyz, a1, a2);
    const spec = std.dot(std.reflect(std.neg(L), N), V);
    col = std.mix(col, P_LEAD_WHITE.$, std.step(0.9, spec) * 0.85);
    hatchK = 0.7;
  } else if (mat === 13) {
    const H = std.normalize(L.add(V));
    col = d.vec3f(look.rampDye[1].xyz);
    col = std.mix(col, P_GLINT.$, std.step(0.9, std.dot(N, H)));
    hatchK = 0.0;
  } else {
    const ri = std.min(MATR_OF.$[mat], 11) * 3;
    col = ramp3(frameLayout.$.mats[ri].xyz, frameLayout.$.mats[ri + 1].xyz, frameLayout.$.mats[ri + 2].xyz, a1, a2);
    if (mat === 10) {
      // wood grain along the part spine
      const g = lineMask(input.pat.y * circ * 22.0 + 0.3 * vnoise(d.vec2f(input.pat.x * 8.0, 0.0)), 0.18, 0.08);
      col = std.mix(col, P_INK.$, g * 0.25);
    } else if (mat === 14) {
      // parchment prop: rows of ink «text» squiggles
      const row = lineMask(input.pat.x * 2.0 * 30.0, 0.25, 0.12) * std.step(0.3, vnoise(d.vec2f(input.pat.y * circ * 90.0, input.pat.x * 60.0)));
      col = std.mix(col, P_INK_FADED.$, row * 0.7);
    } else if (mat === 18) {
      // snail shell: spiral bands along the spiral parameter
      const sp = lineMask(input.pat.x * 2.0 * 7.0 + jitter * 0.08, 0.3, 0.06);
      col = std.mix(col, P_UMBER.$.mul(1.4), sp * 0.7);
    } else if (mat === 16) {
      hatchK = 0.0;
    } else if (mat === 15) {
      const h1 = hatchLines(c1, ppu1, sl.hatch.y, 0.9);
      col = std.mix(col, P_INK.$, h1 * 0.2 * std.select(d.f32(1), d.f32(0), band === 2));
    }
  }

  if (shaded > 0.5) {
    // B: tempera value wobble + facet mosaic
    col = col.mul(1.0 + jitter * 0.06 + (facetH - 0.5) * 0.09);
    // E: cross-hatching in the shadow band (object space; never screen space)
    if (hatchK > 0.0) {
      let hh = d.f32(0);
      if (band === 0) { hh = hatchLines(c1, ppu1, sl.hatch.y, 0.9); }
      if (t < T1 - 0.15 && (bits & 2) !== 0) { hh = std.max(hh, hatchLines(c2, ppu2, sl.hatch.y * 1.2, 0.8)); }
      if ((bits & 4) !== 0) { hh = hh * std.step(0.22, vnoise(d.vec2f(c2 * 30.0, c1 * 3.0))); }
      col = std.mix(col, P_INK.$, hh * 0.45 * sl.shadowTint.w * hatchK);
    }
    // F: faceted rim — whole facets near the silhouette light up
    const fr = 1.0 - std.saturate(std.dot(N, V));
    const rim = std.step(sl.hatch.x, fr) * std.select(d.f32(0.4), d.f32(1), std.dot(N, L) < 0.2);
    // the gold rim separates dark figures from the lapis; on pale cloth it would read as a stain
    const pale = std.smoothstep(0.35, 0.75, luma(col));
    col = std.mix(col, sl.rim.xyz, rim * sl.rim.w * rimK * (1.0 - 0.75 * pale));
  }

  // H: hit flash (before lines: lines stay on top) and status tints
  col = std.mix(col, look.fxColor.xyz, look.fx.x);
  if (look.pattern.w === 3) { col = std.mix(col, P_LAPIS_PALE.$, 0.45); }

  // G: barycentric feature lines (authored edgeMask), quill pressure varies along the line in object space
  const em = input.edge & 7;
  if (em !== 0) {
    const lw = d.f32((input.edge >>> 8) & 255) / 127.5;
    const wl = look.line.y * pxScale * (0.7 + 0.6 * vnoise3(input.rest.mul(13.0))) * std.max(lw, 0.35) * 0.55;
    const dpx = input.bary.div(std.max(fwBary, d.vec3f(0.00001)));
    let ink = d.f32(0);
    if ((em & 1) !== 0) { ink = std.max(ink, 1.0 - std.smoothstep(wl - 0.6, wl + 0.6, dpx.x)); }
    if ((em & 2) !== 0) { ink = std.max(ink, 1.0 - std.smoothstep(wl - 0.6, wl + 0.6, dpx.y)); }
    if ((em & 4) !== 0) { ink = std.max(ink, 1.0 - std.smoothstep(wl - 0.6, wl + 0.6, dpx.z)); }
    col = std.mix(col, std.select(P_INK.$, P_INK_FADED.$, mat === 1), ink * 0.92);
  }

  // I: sketch-to-paint reveal (lines exist before the paint)
  const prog = look.fx.y;
  if (prog < 0.999) {
    const sk = std.mix(P_VELLUM.$, P_INK_FADED.$, hatchLines(c1, ppu1, sl.hatch.y, 0.8) * std.select(d.f32(0.25), d.f32(0), band === 2));
    const nr = vnoise3(input.rest.mul(4.0)) * 0.8 + input.pat.x * 0.4;
    const m = std.smoothstep(nr - 0.04, nr + 0.04, prog * 1.08 - 0.04);
    const pool = 1.0 - std.smoothstep(0.0, 0.25, std.abs(m - 0.5));
    col = std.mix(sk, col, m).mul(1.0 - pool * 0.15);
  }

  // debug views (rdebug)
  const view = sl.qualityFlags.w;
  if (view === 1) {
    col = std.select(std.select(d.vec3f(0.93, 0.88, 0.75), d.vec3f(0.58, 0.35, 0.07), band === 1), d.vec3f(0.02, 0.04, 0.2), band === 0);
  } else if (view === 3) {
    col = N.mul(0.5).add(d.vec3f(0.5));
  } else if (view === 7) {
    col = d.vec3f(stripeM);
  } else if (view === 4) {
    col = d.vec3f(d.f32(mat) / 20.0, d.f32(region) / 10.0, d.f32(input.slot) / 11.0);
  } else if (view === 8) {
    col = d.vec3f(hatchLines(c1, ppu1, sl.hatch.y, 0.9), std.select(d.f32(0), d.f32(1), band === 0), ppu1 / 1000.0);
  }
  return d.vec4f(col, figureMask(input.slot));
});
